/** @jsx jsx */
import { jsx } from '@emotion/react';

import { Fragment, type MutableRefObject, useRef } from 'react';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import VisibilityTracking from 'bundles/page-config-common/components/shared/VisibilityTracking';
import PageConfigComponentMetadataProvider from 'bundles/page-config-common/providers/PageConfigComponentMetadataProvider';
import type { PageConfig_ComponentConfigFragment as PageConfigComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

type Props = {
  pageConfigComponents: Array<PageConfigComponent | null | undefined>;
  renderComponent: (
    pageConfigComponent: PageConfigComponent,
    pageConfigComponents: Array<PageConfigComponent | null | undefined>,
    index: number,
    trackableContentRef?: MutableRefObject<HTMLDivElement | null>
  ) => JSX.Element | null;
};

type PageConfigComponentRendererElementProps = Props & {
  pageConfigComponent: PageConfigComponent;
  index: number;
};

const PageConfigComponentRendererElement = (props: PageConfigComponentRendererElementProps) => {
  const { pageConfigComponent, index, pageConfigComponents, renderComponent } = props;

  const trackableContentRef = useRef<HTMLDivElement | null>(null);

  return (
    <PageConfigComponentMetadataProvider
      id={pageConfigComponent.id}
      index={index}
      name={pageConfigComponent.__typename}
      recommender={{
        context: pageConfigComponent.rankingContext ?? undefined,
        hash: pageConfigComponent.recommenderConfigurationHash ?? '',
      }}
    >
      <VisibilityTracking
        pageConfigComponent={pageConfigComponent}
        index={index}
        trackableContentRef={trackableContentRef}
      >
        <ErrorBoundaryWithTagging componentName={pageConfigComponent.__typename}>
          {renderComponent(pageConfigComponent, pageConfigComponents, index, trackableContentRef)}
        </ErrorBoundaryWithTagging>
      </VisibilityTracking>
    </PageConfigComponentMetadataProvider>
  );
};

const PageConfigComponentRendererWrapper = (props: Props) => {
  const { pageConfigComponents, renderComponent } = props;

  return (
    <Fragment>
      {pageConfigComponents.map((pageConfigComponent, index) => {
        if (!pageConfigComponent) {
          return null;
        }

        return (
          <PageConfigComponentRendererElement
            key={pageConfigComponent.id}
            pageConfigComponents={pageConfigComponents}
            renderComponent={renderComponent}
            pageConfigComponent={pageConfigComponent}
            index={index}
          />
        );
      })}
    </Fragment>
  );
};

export default PageConfigComponentRendererWrapper;
